import type { record } from '@rrweb/record';
import type { eventWithTime } from '@rrweb/types';
import { BASE_REDACT_SELECTOR, HOST_SELECTOR } from './constants';

export type RrEvent = eventWithTime;
export type RecordOptions = NonNullable<Parameters<typeof record<RrEvent>>[0]>;

export interface RedactionConfig {
  /** Vollständiger Redaction-Selektor (inkl. `.relyo-redact`). */
  redactSelector: string;
  maskAllInputs: boolean;
}

/** Eingabetypen, die immer maskiert werden (Spec Kap. 9). */
const ALWAYS_MASKED_TYPES = new Set(['password', 'email', 'tel']);

/** Prüft, ob ein CSS-Selektor gültig ist. */
export function isValidSelector(selector: string): boolean {
  try {
    document.createDocumentFragment().querySelector(selector);
    return true;
  } catch {
    return false;
  }
}

/** `.relyo-redact` plus der optionale Selektor des Projekts. */
export function buildRedactSelector(extra?: string | null): string {
  const trimmed = extra?.trim();
  return trimmed ? `${BASE_REDACT_SELECTOR}, ${trimmed}` : BASE_REDACT_SELECTOR;
}

function autocompleteTokens(el: Element): string[] {
  return (el.getAttribute('autocomplete') ?? '').toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * Entscheidet, ob ein Eingabefeld maskiert wird. Dieselbe Funktion nutzt ab Phase 2 auch
 * Remote Control, um Eingaben in redaktierte Felder abzulehnen.
 */
export function isSensitiveInput(el: Element, cfg: RedactionConfig): boolean {
  if (cfg.maskAllInputs) return true;
  // rrweb markiert Passwortfelder, deren Typ (Passwort anzeigen) umgeschaltet wurde.
  if (el.hasAttribute('data-rr-is-password')) return true;
  const type = (el.getAttribute('type') ?? '').toLowerCase();
  if (ALWAYS_MASKED_TYPES.has(type)) return true;
  if (autocompleteTokens(el).some((t) => t.startsWith('cc-') || t === 'one-time-code')) {
    return true;
  }
  try {
    if (el.closest(cfg.redactSelector)) return true;
  } catch {
    // Ungültiger Selektor: im Zweifel maskieren.
    return true;
  }
  return false;
}

export function maskValue(text: string): string {
  return '*'.repeat(text.length);
}

/**
 * rrweb ruft `maskInputFn` nur für Felder auf, deren Typ oder Tag in `maskInputOptions` steht.
 * Damit auch `autocomplete="cc-*"`, `one-time-code` und `redactSelector`-Treffer maskiert werden,
 * aktivieren wir alle Text-artigen Typen und entscheiden in `maskInputFn` selbst. Unkritische
 * Felder kommen unverändert zurück. `select` bleibt bewusst aus, weil rrweb sonst das
 * `selected`-Attribut aller Optionen entfernt.
 */
export const MASK_INPUT_OPTIONS = {
  password: true,
  email: true,
  tel: true,
  text: true,
  search: true,
  url: true,
  number: true,
  color: true,
  date: true,
  'datetime-local': true,
  month: true,
  range: true,
  time: true,
  week: true,
  textarea: true,
} as const satisfies RecordOptions['maskInputOptions'];

export function createMaskInputFn(cfg: RedactionConfig): (text: string, el: HTMLElement) => string {
  return (text, el) => (isSensitiveInput(el, cfg) ? maskValue(text) : text);
}

/** rrweb-`record()`-Optionen laut Spec Kap. 5 Punkt 4 und Kap. 9. */
export function buildRecordOptions(
  cfg: RedactionConfig,
  emit: (e: RrEvent, isCheckout?: boolean) => void,
): RecordOptions {
  return {
    emit,
    maskAllInputs: cfg.maskAllInputs,
    maskInputOptions: { ...MASK_INPUT_OPTIONS },
    maskInputFn: createMaskInputFn(cfg),
    maskTextSelector: cfg.redactSelector,
    blockSelector: `${cfg.redactSelector}, ${HOST_SELECTOR}`,
    inlineStylesheet: true,
    inlineImages: true,
    collectFonts: true,
    recordCanvas: false,
    // Kein input: 'last' – das meldet Werte erst beim Verlassen des Feldes, der Agent soll live mitlesen.
    sampling: { mousemove: 50, scroll: 150 },
  };
}
